import { defineStore } from 'pinia';
import { client, getEditorialSections } from '@/shared/api/experience';
import type { Article, GeoItem } from '@/shared/models/journal';
import { normalizeArticle } from './transforms';

type CountryDto = {
  slug?: string;
  name?: string;
  name_ru?: string;
};

type CountriesResponse = CountryDto[] | { results?: CountryDto[] };

type ArticlesResponse = {
  results?: Array<Parameters<typeof normalizeArticle>[0]>;
};

export const useJournalStore = defineStore('journal', {
  state: () => ({
    countries: [] as GeoItem[],
    articles: [] as Article[],
    size: 5,
    popularArticleReqSize: 15,
    isViewButton: false,
    isLoadingCountries: false,
    isLoadingArticles: false,
    isLoadingEditorial: false,
    editorialArticles: [] as Article[],
    editorialTitle: '',
  }),
  actions: {
    async getCountries() {
      this.isLoadingCountries = true;

      try {
        const response = await client.get<CountriesResponse>({ url: '/service/journal/api/v1/articles/countries' });
        const countries = Array.isArray(response.data) ? response.data : (response.data?.results ?? []);

        this.countries = countries
          .filter((countryData) => Boolean(countryData.slug && (countryData.name_ru || countryData.name)))
          .map((countryData) => ({
            nameRu: countryData.name_ru || countryData.name || '',
            slug: countryData.slug || '',
          }));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(error);
        this.isLoadingCountries = false;

        return;
      }
      this.isLoadingCountries = false;
    },

    async getArticles() {
      if (this.articles.length || this.isLoadingArticles) {
        return;
      }

      this.isLoadingArticles = true;
      try {
        const response = await client.get<ArticlesResponse>({
          url: '/service/journal/api/v1/articles',
          query: {
            type: 'article',
            ordering: '-published_at',
            page_size: this.popularArticleReqSize,
          },
        });
        this.articles = response.data?.results?.map((articleData) => normalizeArticle(articleData)) ?? [];
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(error);
        this.isLoadingArticles = false;

        return;
      }
      this.isLoadingArticles = false;
    },
    async getEditorialArticles() {
      let response;

      this.isLoadingEditorial = true;

      try {
        response = await getEditorialSections();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.debug(error);
        this.isLoadingEditorial = false;

        return;
      }

      const { data } = response;

      if (!data || !data.length) {
        this.isLoadingEditorial = false;

        return;
      }

      const firstSection = data[0];
      this.editorialArticles = firstSection.articles_data?.map((article) => normalizeArticle(article)) ?? [];
      this.editorialTitle = firstSection.title ?? '';
      this.isLoadingEditorial = false;
    },
  },
});
